import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const DAYS = [
  ['1', 'Lun'], ['2', 'Mar'], ['3', 'Mié'], ['4', 'Jue'],
  ['5', 'Vie'], ['6', 'Sáb'], ['7', 'Dom']
];

const emptyForm = {
  name: '',
  type: 'FLEXIBLE',
  startTime: '08:00',
  endTime: '17:00',
  toleranceMinutes: 15,
  toleranceEntryMinutes: 15,
  toleranceExitMinutes: 15,
  workDays: ['1', '2', '3', '4', '5'],
  breakMinutes: 0,
  weeklyTargetHours: 40,
  maxJornadaHours: 14,
  crossesMidnight: false,
  nightShift: false,
  status: 'ACTIVO'
};

export default function WorkScheduleManagement({ token, isDarkMode, companyId }) {
  const [schedules, setSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState({ userId: '', workScheduleId: '', startDate: new Date().toISOString().slice(0, 10), endDate: '', priority: 1 });
  const [message, setMessage] = useState('');
  const editorRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const api = '/api/work-schedules';

  const colors = {
    text: isDarkMode ? '#e2e8f0' : '#1a202c',
    muted: isDarkMode ? '#94a3b8' : '#718096',
    card: isDarkMode ? '#131924' : '#ffffff',
    input: isDarkMode ? '#0f172a' : '#ffffff',
    border: isDarkMode ? '#334155' : '#e2e8f0'
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = companyId ? { companyId } : {};
      const [s, a, u] = await Promise.all([
        axios.get(api, { headers, params }),
        axios.get(`${api}/assignments`, { headers, params }),
        axios.get('/api/users/all-with-attendance', { headers })
      ]);
      setSchedules(Array.isArray(s.data) ? s.data : []);
      setAssignments(Array.isArray(a.data) ? a.data : []);
      setEmployees(Array.isArray(u.data) ? u.data.filter(x => x.role !== 'SuperAdministrador') : []);
      setMessage('');
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || 'No fue posible cargar las políticas de jornada.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token, companyId]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleDay = day => {
    setForm(prev => ({
      ...prev,
      workDays: prev.workDays.includes(day)
        ? prev.workDays.filter(x => x !== day)
        : [...prev.workDays, day].sort()
    }));
  };

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim()) return setMessage('El nombre de la política es obligatorio.');
    if (form.type === 'FIJO' && (!form.startTime || !form.endTime)) return setMessage('La jornada fija requiere entrada y salida.');

    setSaving(true);
    try {
      const payload = {
        ...form,
        workDays: form.workDays.join(','),
        companyId: companyId || undefined,
        toleranceMinutes: Number(form.toleranceMinutes),
        toleranceEntryMinutes: Number(form.toleranceEntryMinutes),
        toleranceExitMinutes: Number(form.toleranceExitMinutes),
        breakMinutes: Number(form.breakMinutes),
        weeklyTargetHours: form.weeklyTargetHours === '' ? null : Number(form.weeklyTargetHours),
        maxJornadaHours: Number(form.maxJornadaHours)
      };
      if (editingId) await axios.put(`${api}/${editingId}`, payload, { headers });
      else await axios.post(api, payload, { headers });
      setMessage(editingId ? 'Política actualizada correctamente.' : 'Política creada correctamente.');
      reset();
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible guardar la política.');
    } finally {
      setSaving(false);
    }
  };

  const edit = row => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      type: row.type || 'FLEXIBLE',
      startTime: row.startTime || '08:00',
      endTime: row.endTime || '17:00',
      toleranceMinutes: Number(row.toleranceMinutes ?? 15),
      toleranceEntryMinutes: Number(row.toleranceEntryMinutes ?? row.toleranceMinutes ?? 15),
      toleranceExitMinutes: Number(row.toleranceExitMinutes ?? row.toleranceMinutes ?? 15),
      workDays: String(row.workDays || '1,2,3,4,5').split(',').filter(Boolean),
      breakMinutes: Number(row.breakMinutes ?? 0),
      weeklyTargetHours: row.weeklyTargetHours ?? 40,
      maxJornadaHours: Number(row.maxJornadaHours ?? 14),
      crossesMidnight: Boolean(row.crossesMidnight),
      nightShift: Boolean(row.nightShift),
      status: row.status || 'ACTIVO'
    });
    requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      editorRef.current?.querySelector('input')?.focus({ preventScroll: true });
    });
  };

  const deactivate = async id => {
    if (!window.confirm('¿Desactivar esta política?')) return;
    try {
      await axios.delete(`${api}/${id}`, { headers, data: { companyId: companyId || undefined } });
      setMessage('Política desactivada.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible desactivar la política.');
    }
  };

  const activate = async id => {
    if (!window.confirm('¿Activar nuevamente esta jornada?')) return;
    try {
      await axios.put(`${api}/${id}/activate`, { companyId: companyId || undefined }, { headers });
      setMessage('Jornada activada correctamente.');
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible activar la jornada.');
    }
  };

  const assign = async e => {
    e.preventDefault();
    if (!assignment.userId || !assignment.workScheduleId || !assignment.startDate) {
      return setMessage('Selecciona empleado, jornada y fecha inicial.');
    }
    try {
      await axios.post(`${api}/assignments`, {
        ...assignment,
        companyId: companyId || undefined,
        userId: Number(assignment.userId),
        workScheduleId: Number(assignment.workScheduleId),
        priority: Number(assignment.priority)
      }, { headers });
      setMessage('Jornada asignada correctamente.');
      setAssignment({ ...assignment, userId: '', workScheduleId: '', endDate: '', priority: 1 });
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible asignar la jornada.');
    }
  };

  const removeAssignment = async id => {
    if (!window.confirm('¿Retirar esta asignación?')) return;
    try {
      await axios.delete(`${api}/assignments/${id}`, { headers, params: companyId ? { companyId } : {} });
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible retirar la asignación.');
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '9px 10px',
    borderRadius: 7, border: `1px solid ${colors.border}`,
    background: colors.input, color: colors.text
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: colors.muted, marginBottom: 5 };

  return (
    <section style={{ background: colors.card, color: colors.text, padding: 22, borderRadius: 12, border: `1px solid ${colors.border}`, marginBottom: 30 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>🕒 Políticas de Jornada</h2>
        <p style={{ margin: '5px 0 0', color: colors.muted, fontSize: 13 }}>
          Define jornadas flexibles o de horario fijo, tolerancias, días laborales y turnos que cruzan medianoche.
        </p>
      </div>

      {message && (
        <div style={{ padding: 10, marginBottom: 15, borderRadius: 7, border: `1px solid ${colors.border}`, background: isDarkMode ? '#172033' : '#f7fafc' }}>
          {message}
        </div>
      )}

      <div ref={editorRef} id="work-schedule-editor" style={{ scrollMarginTop: 90 }}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 15 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
          <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={form.name} onChange={e => update('name', e.target.value)} placeholder="Ej. Administrativos 08:00–17:00" /></div>
          <div><label style={labelStyle}>Política</label><select style={inputStyle} value={form.type} onChange={e => update('type', e.target.value)}><option value="FLEXIBLE">Flexible</option><option value="FIJO">Horario fijo</option></select></div>
          <div><label style={labelStyle}>Entrada</label><input type="time" style={inputStyle} value={form.startTime || ''} onChange={e => update('startTime', e.target.value)} disabled={form.type === 'FLEXIBLE'} /></div>
          <div><label style={labelStyle}>Salida</label><input type="time" style={inputStyle} value={form.endTime || ''} onChange={e => update('endTime', e.target.value)} disabled={form.type === 'FLEXIBLE'} /></div>
        </div>

        <div>
          <label style={labelStyle}>Días laborales</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {DAYS.map(([value, label]) => (
              <button key={value} type="button" onClick={() => toggleDay(value)} style={{
                padding: '7px 11px', borderRadius: 18, cursor: 'pointer', fontWeight: 700,
                border: `1px solid ${colors.border}`,
                background: form.workDays.includes(value) ? '#3182ce' : (isDarkMode ? '#1e293b' : '#edf2f7'),
                color: form.workDays.includes(value) ? '#fff' : colors.text
              }}>{label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
          <div><label style={labelStyle}>Tolerancia general (min)</label><input type="number" min="0" max="1440" style={inputStyle} value={form.toleranceMinutes} onChange={e => update('toleranceMinutes', e.target.value)} /></div>
          <div><label style={labelStyle}>Tolerancia entrada (min)</label><input type="number" min="0" max="1440" style={inputStyle} value={form.toleranceEntryMinutes} onChange={e => update('toleranceEntryMinutes', e.target.value)} /></div>
          <div><label style={labelStyle}>Tolerancia salida (min)</label><input type="number" min="0" max="1440" style={inputStyle} value={form.toleranceExitMinutes} onChange={e => update('toleranceExitMinutes', e.target.value)} /></div>
          <div><label style={labelStyle}>Descanso (min)</label><input type="number" min="0" max="1440" style={inputStyle} value={form.breakMinutes} onChange={e => update('breakMinutes', e.target.value)} /></div>
          <div><label style={labelStyle}>Objetivo semanal (h)</label><input type="number" min="0" max="168" step="0.25" style={inputStyle} value={form.weeklyTargetHours} onChange={e => update('weeklyTargetHours', e.target.value)} /></div>
          <div><label style={labelStyle}>Máximo de jornada (h)</label><input type="number" min="1" max="48" step="0.25" style={inputStyle} value={form.maxJornadaHours} onChange={e => update('maxJornadaHours', e.target.value)} /><div style={{ marginTop: 4, fontSize: 10, color: colors.muted }}>Por defecto: 14 h. Al superar este límite, la jornada queda pendiente de confirmación.</div></div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15 }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={form.crossesMidnight} onChange={e => update('crossesMidnight', e.target.checked)} /> Cruza medianoche</label>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={form.nightShift} onChange={e => update('nightShift', e.target.checked)} /> Turno nocturno</label>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="submit" disabled={saving} style={{ padding: '9px 16px', border: 0, borderRadius: 7, background: '#3182ce', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Guardando…' : editingId ? '💾 Actualizar política' : '➕ Crear política'}
          </button>
          {editingId && <button type="button" onClick={reset} style={{ padding: '9px 16px', border: `1px solid ${colors.border}`, borderRadius: 7, background: 'transparent', color: colors.text, cursor: 'pointer' }}>Cancelar</button>}
        </div>
      </form>
      </div>

      <hr style={{ border: 0, borderTop: `1px solid ${colors.border}`, margin: '25px 0' }} />

      <h3 style={{ marginBottom: 12 }}>Catálogo de políticas</h3>
      {loading ? <p style={{ color: colors.muted }}>Cargando…</p> : (
        <div style={{ display: 'grid', gap: 10 }}>
          {schedules.map(row => (
            <div key={row.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 9, padding: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <strong>{row.name}</strong>
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '3px 7px', borderRadius: 12, border: `1px solid ${colors.border}` }}>
                    {row.status === 'ACTIVO' ? 'ACTIVA' : 'INACTIVA'}
                  </span>
                  <div style={{ fontSize: 12, color: colors.muted, marginTop: 4 }}>
                    {row.type === 'FLEXIBLE' ? 'Flexible' : `Fijo ${row.startTime}–${row.endTime}`}
                    {' · '}Tolerancia {row.toleranceMinutes} min
                    {' · '}Días {row.workDays}
                    {' · '}Máximo {Number(row.maxJornadaHours ?? 14)} h
                    {row.crossesMidnight ? ' · Cruza medianoche' : ''}
                    {row.nightShift ? ' · Nocturna' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 7 }}>
                  <button type="button" onClick={() => edit(row)} style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer' }}>✏️ Editar</button>
                  {row.status === 'ACTIVO' ? (
                    <button type="button" onClick={() => deactivate(row.id)} style={{ padding: '6px 10px', border: 0, borderRadius: 6, background: '#c53030', color: '#fff', cursor: 'pointer' }}>Desactivar</button>
                  ) : (
                    <button type="button" onClick={() => activate(row.id)} style={{ padding: '6px 10px', border: 0, borderRadius: 6, background: '#38a169', color: '#fff', cursor: 'pointer' }}>Activar</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {!schedules.length && <p style={{ color: colors.muted }}>No hay políticas configuradas.</p>}
        </div>
      )}

      <hr style={{ border: 0, borderTop: `1px solid ${colors.border}`, margin: '25px 0' }} />

      <h3>Asignación por empleado</h3>
      <p style={{ color: colors.muted, fontSize: 13 }}>La asignación tiene vigencia por fechas. Una prioridad mayor permite una excepción temporal sobre otra asignación.</p>
      <form onSubmit={assign} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10, alignItems: 'end' }}>
        <div><label style={labelStyle}>Empleado</label><select style={inputStyle} value={assignment.userId} onChange={e => setAssignment({ ...assignment, userId: e.target.value })}><option value="">Seleccionar…</option>{employees.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}</select></div>
        <div><label style={labelStyle}>Jornada</label><select style={inputStyle} value={assignment.workScheduleId} onChange={e => setAssignment({ ...assignment, workScheduleId: e.target.value })}><option value="">Seleccionar…</option>{schedules.filter(s => s.status === 'ACTIVO').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        <div><label style={labelStyle}>Desde</label><input type="date" style={inputStyle} value={assignment.startDate} onChange={e => setAssignment({ ...assignment, startDate: e.target.value })} /></div>
        <div><label style={labelStyle}>Hasta</label><input type="date" style={inputStyle} value={assignment.endDate} onChange={e => setAssignment({ ...assignment, endDate: e.target.value })} /></div>
        <div><label style={labelStyle}>Prioridad</label><input type="number" min="1" max="99" style={inputStyle} value={assignment.priority} onChange={e => setAssignment({ ...assignment, priority: e.target.value })} /></div>
        <button type="submit" style={{ padding: '9px 12px', border: 0, borderRadius: 7, background: '#38a169', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Asignar</button>
      </form>

      <div style={{ marginTop: 15, display: 'grid', gap: 8 }}>
        {assignments.map(a => (
          <div key={a.id} style={{ padding: 10, border: `1px solid ${colors.border}`, borderRadius: 8, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <strong>{a.user?.name || `Usuario ${a.userId}`}</strong>
              <div style={{ fontSize: 12, color: colors.muted }}>{a.schedule?.name || `Jornada ${a.workScheduleId}`} · {a.startDate} → {a.endDate || 'sin fecha final'} · prioridad {a.priority}</div>
            </div>
            {a.status === 'ACTIVO' && <button type="button" onClick={() => removeAssignment(a.id)} style={{ padding: '6px 10px', border: 0, borderRadius: 6, background: '#c53030', color: '#fff', cursor: 'pointer' }}>Retirar</button>}
          </div>
        ))}
        {!assignments.length && <p style={{ color: colors.muted }}>No hay asignaciones registradas.</p>}
      </div>
    </section>
  );
}
